/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import React, { ChangeEvent, forwardRef } from 'react';

import { cn } from '@/lib/utils';

import { Input } from '../ui/input';
import Icons from '../../icons/icons';

interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  value: string;
  onClear: () => void;
  placeholder?: string;
  className?: string;
  icon?: React.ReactNode;
  inputClassName?: string;
  clearButtonClassName?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      onChange,
      value,
      onClear,
      placeholder = 'Search',
      className = '',
      icon = Icons.Search(),
      inputClassName = '',
      clearButtonClassName = '',
      ...props
    },
    ref
  ) => {
    return (
      <div
        className={cn(
          'flex h-7 w-[178px] items-center gap-1.5 rounded-sm border border-form-control-border-rest p-2 pl-3 focus-within:!border-form-control-border-focus hover:border-form-control-border-hover',
          className
        )}
      >
        <span>{icon}</span>
        <Input
          ref={ref}
          onChange={onChange}
          placeholder={placeholder}
          className={cn(
            'border-none p-0 text-form-input-text text-form-input-rest focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-[#8C9EA1]',
            inputClassName
          )}
          value={value}
          {...props}
        />
        {value && value.toString().length > 0 && (
          <span
            onClick={onClear}
            className={cn('cursor-pointer', clearButtonClassName)}
          >
            {Icons.Close('size-4 text-form-control-border-rest')}
          </span>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
